import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { WarningCircle } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { useAuth } from "../hooks/helper/useAuth";
import Loading from "../components/Loading";
import { IViewAsPayload } from "../interfaces";
import brandLogo from "../assets/images/Logo.png";
import { MeshBackground, useGlobalStyles, IcoArrow } from "./AuthShared";
import ThemeToggle from "../components/ThemeToggle";

const EASE = [0.22, 1, 0.36, 1];

const TakeOverLogin: React.FC = () => {
  useGlobalStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { takeOverLogin, isLoading: authLoading } = useAuth();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");
  const accessToken = searchParams.get("accessToken");

  useEffect(() => {
    const handleTakeOverLogin = async () => {
      if (!token) {
        setError("No token provided");
        return;
      }

      if (!accessToken) {
        setError("No access token provided");
        return;
      }

      if (authLoading || isProcessing) {
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        const payload: IViewAsPayload = {
          encryptedEmail: String(token.replace(/ /g, "+")) || "",
          accessToken: String(accessToken.replace(/ /g, "+")) || "",
        };

        await takeOverLogin(payload);

        toast.success("Login successful");
        navigate("/dashboard");
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Take over login failed";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    handleTakeOverLogin();
  }, [token, takeOverLogin, authLoading, navigate, accessToken]);

  /* ── Loading state ───────────────────────────────────────────────────── */
  if (isProcessing || authLoading) {
    return <Loading />;
  }

  /* ── Error state ─────────────────────────────────────────────────────── */
  if (error || !token) {
    return (
      <div className="fixed inset-0 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100 dark:bg-neutral-950 [font-family:var(--font-geist)]">
        <MeshBackground />

        <div className="absolute top-4 right-4 z-[10]">
          <ThemeToggle />
        </div>

        <div className="relative flex-1 flex items-center justify-center px-4 z-[2]">
          <div className="w-full" style={{ maxWidth: "clamp(360px, 90vw, 460px)" }}>

            {/* Logo */}
            <motion.div
              className="flex justify-center mb-5"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <img src={brandLogo} alt="AppRevBooster" className="h-12 object-contain" />
            </motion.div>

            {/* Glass card */}
            <motion.div
              className="relative overflow-hidden p-[24px_24px_22px] sm:p-[28px_28px_26px] bg-white/[0.88] dark:bg-neutral-900/95 backdrop-blur-[28px] saturate-[160%] border border-black/[0.09] dark:border-neutral-700 rounded-2xl shadow-xl"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08, ease: EASE }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #1a3d7c, transparent)",
                  backgroundSize: "200% 100%",
                  animation: "arb-glow-trail 4s linear infinite",
                }}
              />

              <motion.div
                className="flex flex-col items-center text-center gap-4 py-2"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15, ease: EASE }}
              >
                <motion.div
                  className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.22, ease: EASE }}
                >
                  <WarningCircle size={32} weight="duotone" className="text-red-500" />
                </motion.div>

                <motion.div
                  className="flex flex-col gap-1"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.3, ease: EASE }}
                >
                  <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    {!token ? "Invalid Token" : "Login Failed"}
                  </h2>
                  <p className="text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
                    {!token
                      ? "No token was provided in the URL. Please check your link and try again."
                      : error || "An error occurred during login. Please try again."}
                  </p>
                </motion.div>

                <motion.div
                  className="w-full"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.38, ease: EASE }}
                >
                  <button
                    type="button"
                    onClick={() => navigate("/auth")}
                    className="mt-1 w-full py-3.5 px-5 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-200 min-h-[49px] bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-[0_2px_14px_rgba(26,61,124,0.28)] hover:shadow-[0_4px_20px_rgba(26,61,124,0.38)] hover:-translate-y-px active:translate-y-0 cursor-pointer border-none [font-family:inherit]"
                  >
                    Go to Login
                    <IcoArrow size={16} />
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom status bar */}
        <motion.div
          className="relative flex-shrink-0 z-[4] flex flex-wrap justify-center gap-x-3 gap-y-1 py-3 text-[11px] sm:text-[11.5px] text-gray-400 [font-family:var(--font-mono)] tracking-[0.3px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5, ease: EASE }}
        >
          <a href="https://apprevbooster.com/privacy-policy" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Privacy</a>
          <span>·</span>
          <a href="https://apprevbooster.com/terms-of-service" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Terms</a>
        </motion.div>
      </div>
    );
  }

  /* ── Fallback ─────────────────────────────────────────────────────────── */
  return <Loading />;
};

export default TakeOverLogin;
